// Dashboard widgets fed by /api/myDonation/apiInsights (counts only) plus a short upcoming-
// anniversaries list (a named list — shown only with the "View donation" permission).
// Every count links to its full list on the Reports page when the user may see names.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Banknote,
  Cake,
  CalendarClock,
  ChevronRight,
  FileWarning,
  Repeat,
  UserMinus,
  UserPlus,
  UserRoundCheck,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useAuth } from "@/lib/auth";
import {
  fetchInsightList,
  formatAmount,
  type Band,
  type CountAmount,
  type InsightList,
  type InsightListType,
  type Insights,
} from "@/lib/donation";
import { formatDate } from "@/lib/format";
import { useSessionScope } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BlockPlaceholder, Busy } from "@/components/busy";
import { DonorLink } from "@/components/donor-link";
import { PhoneValue } from "@/components/contact-value";

export function reportHref(list: InsightListType, fyId: number | null, days?: number, causeId?: number | null) {
  const q = new URLSearchParams({ list });
  if (fyId != null) q.set("fy", String(fyId));
  if (causeId) q.set("cause", String(causeId));
  if (days) q.set("days", String(days));
  return `/reports?${q.toString()}`;
}

/** Wraps children in a link to the list when allowed, else renders them as-is. */
function MaybeLink({
  to,
  enabled,
  className,
  children,
}: {
  to: string;
  enabled: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return enabled ? (
    <Link to={to} className={cn("block rounded-xl transition-colors hover:bg-muted/50", className)}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );
}

// ------------------------------------------------------------------ donor tiles

export function DonorTiles({
  ins,
  fyId,
  causeId,
  busy = false,
}: {
  ins: Insights;
  fyId: number | null;
  causeId: number | null;
  busy?: boolean;
}) {
  const d = ins.donors;
  const can = ins.canViewDetails;
  const secondPct = d.newCount > 0 ? Math.round((d.repeatDonorCount / d.newCount) * 100) : null;
  const tiles: {
    icon: LucideIcon;
    label: string;
    value: string;
    sub: string;
    list: InsightListType;
    days?: number;
  }[] = [
    {
      icon: UserPlus,
      label: "New donors this FY",
      value: d.newCount.toLocaleString("en-IN"),
      sub: formatAmount(d.newAmount),
      list: "newDonors",
    },
    {
      icon: UserRoundCheck,
      label: "Gave again",
      value: secondPct == null ? "—" : `${secondPct}%`,
      sub: `${d.repeatDonorCount} of ${d.newCount} new donors`,
      list: "newDonors",
    },
    {
      icon: UserMinus,
      label: "Lapsed donors",
      value: d.lapsedCount.toLocaleString("en-IN"),
      sub: `${formatAmount(d.lapsedPrevAmount)} last FY`,
      list: "lapsedDonors",
    },
    {
      icon: Cake,
      label: `Anniversaries · ${d.anniversaryDays} days`,
      value: d.anniversaryCount.toLocaleString("en-IN"),
      sub: "birthdays & anniversaries",
      list: "anniversaries",
      days: d.anniversaryDays,
    },
    {
      icon: CalendarClock,
      label: `Gave a year ago · ${d.anniversaryDays}d`,
      value: d.donationAnniversaryCount.toLocaleString("en-IN"),
      sub: "donation anniversaries",
      list: "donationAnniversaries",
      days: d.anniversaryDays,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
      {tiles.map((t, i) => (
        <Busy key={t.label} busy={busy} compact className={cn(i === tiles.length - 1 && "col-span-2 md:col-span-1")}>
        <Card size="sm" className="h-full py-0">
          <MaybeLink to={reportHref(t.list, fyId, t.days, causeId)} enabled={can} className="h-full">
            <CardContent className="flex flex-col gap-1 py-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <t.icon className="size-3.5 shrink-0" />
                <span className="truncate">{t.label}</span>
                {can && <ChevronRight className="ml-auto size-3.5 shrink-0" />}
              </div>
              <div className="text-xl font-semibold tabular-nums">{t.value}</div>
              <div className="truncate text-xs text-muted-foreground">{t.sub}</div>
            </CardContent>
          </MaybeLink>
        </Card>
        </Busy>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ upcoming anniversaries

export function UpcomingAnniversariesCard({
  ins,
  fyId,
  causeId,
  busy = false,
  onOpenDonor,
}: {
  ins: Insights;
  fyId: number | null;
  causeId: number | null;
  /** The parent insights refetch; the card also tracks its own list fetch. */
  busy?: boolean;
  /** Dashboard: open the donor in a bottom sheet instead of navigating. */
  onOpenDonor?: (donorId: number) => void;
}) {
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const days = ins.donors.anniversaryDays;
  const [list, setList] = useState<InsightList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !scope || !ins.canViewDetails) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchInsightList(token, scope, "anniversaries", { budgetYearId: fyId, causeId, days }, controller.signal)
      .then(setList)
      .catch((e) => {
        if ((e as Error).name !== "AbortError") setError((e as Error).message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, scope, ins.canViewDetails, fyId, causeId, days]);

  return (
    <Busy busy={busy || (loading && !!list)}>
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Upcoming anniversaries</CardTitle>
        <CardDescription>Donors' birthdays and anniversaries in the next {days} days</CardDescription>
      </CardHeader>
      <CardContent>
        {!ins.canViewDetails ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {ins.donors.anniversaryCount} upcoming. Names need the "View donation" permission.
          </p>
        ) : error ? (
          <p className="py-6 text-center text-sm text-destructive">{error}</p>
        ) : !list ? (
          <BlockPlaceholder className="h-40" />
        ) : list.rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">None in the next {days} days.</p>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {list.rows.slice(0, 8).map((r, i) => (
                <li key={i} className="flex items-center gap-3 py-2 text-sm">
                  <span className="w-14 shrink-0 text-xs text-muted-foreground">
                    {r.inDays === 0 ? "Today" : r.inDays === 1 ? "Tomorrow" : formatDate(String(r.date)).replace(/ \d{4}$/, "")}
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* Name opens Call / WhatsApp / Email for this donor. */}
                    <DonorLink
                      name={String(r.name ?? "")}
                      donorId={r.donorId == null ? null : Number(r.donorId)}
                      onOpen={onOpenDonor}
                      className="block"
                    />
                    <div className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                      <span className="truncate">
                        {[r.occasion, r.years ? `${r.years} yrs` : null].filter(Boolean).join(" · ")}
                      </span>
                      {r.mobile && (
                        <>
                          <span>·</span>
                          <PhoneValue value={String(r.mobile)} className="truncate" />
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {list.total > 8 && (
              <Link to={reportHref("anniversaries", fyId, days, causeId)} className="mt-2 inline-block text-xs text-primary">
                View all {list.total}
              </Link>
            )}
          </>
        )}
      </CardContent>
    </Card>
    </Busy>
  );
}

// ------------------------------------------------------------------ donation size bands

export function DonationSizeCard({ bands }: { bands: Band[] }) {
  const total = bands.reduce((s, b) => s + b.count, 0);
  const maxAmount = Math.max(0, ...bands.map((b) => b.amount));
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Donation size</CardTitle>
        <CardDescription>Received this FY, by receipt amount</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-3">
          {bands.map((b) => (
            <li key={b.label} className="flex flex-col gap-1">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{b.label}</span>
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  {b.count} receipts{total > 0 ? ` · ${Math.round((b.count / total) * 100)}%` : ""}
                </span>
                <span className="w-24 shrink-0 text-right font-medium tabular-nums">{formatAmount(b.amount)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-series-current"
                  style={{ width: `${maxAmount > 0 ? (b.amount / maxAmount) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ pipeline / operations

function Row({
  icon: Icon,
  label,
  value,
  sub,
  to,
  enabled,
  warn,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  to?: string;
  enabled: boolean;
  warn?: boolean;
}) {
  const body = (
    <div className="flex items-center gap-3 px-2 py-2.5 text-sm">
      <Icon className={cn("size-4 shrink-0 text-muted-foreground", warn && "text-amber-600 dark:text-amber-400")} />
      <div className="min-w-0 flex-1">
        <div className="truncate">{label}</div>
        {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
      </div>
      <span className="shrink-0 font-medium tabular-nums">{value}</span>
      {to && enabled && <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
    </div>
  );
  return to ? (
    <MaybeLink to={to} enabled={enabled} className="rounded-lg">
      {body}
    </MaybeLink>
  ) : (
    body
  );
}

const ca = (x: CountAmount | null | undefined) => (x ? `${x.count} · ${formatAmount(x.amount)}` : "—");

export function PipelineCard({ ins, fyId, causeId }: { ins: Insights; fyId: number | null; causeId: number | null }) {
  const { pendingAging, pledges, recurring } = ins.pipeline;
  const can = ins.canViewDetails;
  const pendingTotal = pendingAging.reduce((s, b) => s + b.amount, 0);
  const pendingOld = pendingAging.slice(2).reduce((s, b) => s + b.count, 0);
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Pipeline</CardTitle>
        <CardDescription>Money promised but not yet received</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div>
          <MaybeLink to={reportHref("pendingReceipts", fyId, undefined, causeId)} enabled={can} className="-mx-2 px-2 py-1">
            <div className="mb-2 flex items-baseline justify-between text-sm">
              <span className="font-medium">Pending receipts (all years)</span>
              <span className="font-medium tabular-nums">{formatAmount(pendingTotal)}</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {pendingAging.map((b, i) => (
                <div key={b.label} className="rounded-lg bg-muted/60 p-2">
                  <div className={cn("text-[11px] text-muted-foreground", i >= 2 && b.count > 0 && "text-amber-700 dark:text-amber-400")}>
                    {b.label}
                  </div>
                  <div className="text-sm font-semibold tabular-nums">{b.count}</div>
                </div>
              ))}
            </div>
          </MaybeLink>
          {pendingOld > 0 && (
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="size-3.5" /> {pendingOld} pending for over 30 days
            </p>
          )}
        </div>

        <div className="-mx-2 divide-y divide-border">
          <Row
            icon={Wallet}
            label="Open pledges"
            sub={`${formatAmount(pledges.paid)} paid of ${formatAmount(pledges.pledged)}`}
            value={String(pledges.openCount)}
            enabled={can}
          />
          <Row
            icon={AlertTriangle}
            label="Pledge instalments overdue"
            value={ca(pledges.overdue)}
            to={reportHref("pledgeInstallments", fyId, undefined, causeId)}
            enabled={can}
            warn={pledges.overdue.count > 0}
          />
          <Row
            icon={CalendarClock}
            label="Pledge instalments due in 30 days"
            value={ca(pledges.due30)}
            to={reportHref("pledgeInstallments", fyId, undefined, causeId)}
            enabled={can}
          />
          <Row
            icon={Repeat}
            label="Active recurring donors"
            sub={`${formatAmount(recurring.dueThisMonth.amount)} due this month`}
            value={String(recurring.activeCount)}
            enabled={can}
          />
          <Row
            icon={AlertTriangle}
            label="Recurring instalments missed this FY"
            value={ca(recurring.missed)}
            to={reportHref("recurringMissed", fyId, undefined, causeId)}
            enabled={can}
            warn={recurring.missed.count > 0}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function OperationsCard({ ins, fyId, causeId }: { ins: Insights; fyId: number | null; causeId: number | null }) {
  const o = ins.operations;
  const can = ins.canViewDetails;
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Operations &amp; compliance</CardTitle>
        <CardDescription>Things to act on this FY</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="-mx-2 divide-y divide-border">
          {o.cashToday && (
            <Row icon={Banknote} label="Cash collected today" value={ca(o.cashToday)} enabled={can} />
          )}
          <Row
            icon={Banknote}
            label="Cheques not yet deposited"
            value={ca(o.chequesUndeposited)}
            to={reportHref("chequesUndeposited", fyId, undefined, causeId)}
            enabled={can}
            warn={o.chequesUndeposited.count > 0}
          />
          <Row
            icon={FileWarning}
            label="80G receipts without PAN"
            sub="Needed for Form 10BD"
            value={ca(o.eightyGNoPan)}
            to={reportHref("eightyGNoPan", fyId, undefined, causeId)}
            enabled={can}
            warn={o.eightyGNoPan.count > 0}
          />
          <Row
            icon={FileWarning}
            label="Not posted to Tally"
            value={ca(o.tallyPending)}
            to={reportHref("tallyPending", fyId, undefined, causeId)}
            enabled={can}
            warn={o.tallyPending.count > 0}
          />
        </div>
      </CardContent>
    </Card>
  );
}
